import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  OmschrijvingEnBedrag,
  Onderneming,
  SpecificatieOnroerendeZaken,
  SpecificatiePriveGebruikAuto,
  SpecificatiePriveGebruikWoning
} from '@snelstart-ventures/fiscaal/domain-logic';
import {filter, Observable, shareReplay, take, tap} from 'rxjs';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {optionsLandcodesMetNLD} from '../../../../gedeelde-componenten/gedeelde-componenten.component';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {PriveOntrekkingenAutoComponent} from "./onttrekking-privagebruik-auto/prive-ontrekkingen-auto.component";

type AutoFormGroup = FormGroup<{
  autoWordtPriveGebruikt: FormControl<boolean | null>;
  kenteken: FormControl<string | null>;
  catalogusPrijs: FormControl<number | null>;
  periodePriveGebruik: PeriodeFormGroup;
  kilometerAdministratieAanwezig: FormControl<boolean | null>;
  kostenEnLastenVoorEigenRekening: FormControl<boolean | null>;
  priveGebruik: FormControl<number | null>;
}>;

type OnroerendeZaakFormGroup = FormGroup<{
  adresOnroerendeZaak: AdresFormGroup;
  waardeEconomischeVerkeer: FormControl<number | null>;
  boekwaardeOpMonentOnttrekking: FormControl<number | null>;
  transactieMetDerde: FormControl<boolean | null>;
  taxateurBetrokken: FormControl<boolean | null>;
  naamTaxateur: FormControl<string | null>;
}>;

type WoningFormGroup = FormGroup<{
  adresWoning: AutoFormGroup;
  periodeBewoning: PeriodeFormGroup;
  percentageGerechtigd: FormControl<number | null>;
  berekendeOnttrekking: FormControl<number | null>;
  wozWaarde: FormControl<number | null>;
}>;

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

type AdresFormGroup = FormGroup<{
  straat: FormControl<string | null>;
  huisNummer: FormControl<number | null>;
  huisNummerToevoeging: FormControl<string | null>;
  postcode: FormControl<string | null>;
  woonplaats: FormControl<string | null>;
  landCode: FormControl<string | null>;
}>;

type OmschrijvingBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-prive-ontrekkingen-en-stortingen',
  templateUrl: './prive-ontrekkingen-en-stortingen.component.html',
  styleUrls: ['./prive-ontrekkingen-en-stortingen.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule,
    PriveOntrekkingenAutoComponent
  ]
})
export class PriveOntrekkingenEnStortingenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public indexOnderneming = 0;
  public isOndernemer = false;
  public isCheckingRdw = false;
  public isPartner = false;

  public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'}
  ];

  public formgroups = new FormGroup({
    gebruikAuto: new FormArray<AutoFormGroup>([]),
    gebruikWoning: new FormArray<WoningFormGroup>([]),
    onttrekkingOverig: new FormArray<OmschrijvingBedragFormGroup>([]),
    onttrekkingOg: new FormArray<OnroerendeZaakFormGroup>([]),
    stortingenOg: new FormArray<OnroerendeZaakFormGroup>([]),
    stortingenOverig: new FormArray<OmschrijvingBedragFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });

    // Subscribe to changes in FormArrays
    this.privegebruikAuto.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveForm());
    this.privegebruikWoning.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveForm());
    this.onttrekkingWoning.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveForm());
    this.onttrekkingOverig.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveForm());
    this.stortingenOverig.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveForm());
    this.stortingenOverig.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveForm());
  }

  ngOnDestroy(): void {
    this.dataService.calculateLatestIncomeTaxReturn();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: Aangifte): void {
    this.formgroups.reset({}, {emitEvent: false});
    this.isOndernemer = false;
    if (details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)) {
      this.isOndernemer = true;
      this.updateFormAuto(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.onttrekkingPriveGebruikAuto || []);
      this.updateFormWoning(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.onttrekkingPriveGebruikWoning || []);
      this.updateFormOnttrekkingOg(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.onttrekkingOG || []);
      this.updateFormOnttrekkingenOverig(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.onttrekkingenOverig || []);
      this.updateFormStortingenOg(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.stortingOG || []);
      this.updateFormStortingenOverig(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.stortingenOverig || []);
    }
  }

  get privegebruikAuto(): FormArray<AutoFormGroup> {
    return this.formgroups.controls.gebruikAuto;
  }

  addRijAuto(): void {
    this.privegebruikAuto.push(this.createFormGroupAuto(), {emitEvent: false});
  }

  verwijderRijAuto(index: number): void {
    this.privegebruikAuto.removeAt(index, { emitEvent: false });
    this.formgroups.updateValueAndValidity({ emitEvent: false });
    this.formgroups.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  get privegebruikWoning(): FormArray<WoningFormGroup> {
    return this.formgroups.controls.gebruikWoning;
  }

  addRijWoning(): void {
    this.privegebruikWoning.push(this.createFormGroupWoning(), {emitEvent: false});
  }

  verwijderRijWoning(index: number): void {
    this.privegebruikWoning.removeAt(index, { emitEvent: false });
    this.formgroups.updateValueAndValidity({ emitEvent: false });
    this.formgroups.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  get onttrekkingWoning(): FormArray<OnroerendeZaakFormGroup> {
    return this.formgroups.controls.onttrekkingOg;
  }

  addRijOnttrekkingWoning(): void {
    this.onttrekkingWoning.push(this.createFormGroupOnttrekkingWoning(), {emitEvent: false});
  }

  verwijderRijOnttrekkingWoning(index: number): void {
    this.onttrekkingWoning.removeAt(index, { emitEvent: false });
    this.formgroups.updateValueAndValidity({ emitEvent: false });
    this.formgroups.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  get onttrekkingOverig(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formgroups.controls.onttrekkingOverig;
  }

  addRijOnttrekkingOverig(): void {
    this.onttrekkingOverig.push(this.createFormOnttrekkingOverig(), {emitEvent: false});
  }

  verwijderRijOnttrekkingOverig(index: number): void {
    this.onttrekkingOverig.removeAt(index, { emitEvent: false });
    this.formgroups.updateValueAndValidity({ emitEvent: false });
    this.formgroups.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  get stortingWoning(): FormArray<OnroerendeZaakFormGroup> {
    return this.formgroups.controls.stortingenOg;
  }

  addRijStortingWoning(): void {
    this.stortingWoning.push(this.createFormGroupOnttrekkingWoning(), {emitEvent: false});
  }

  verwijderRijStortingWoning(index: number): void {
    this.stortingWoning.removeAt(index, { emitEvent: false });
    this.formgroups.updateValueAndValidity({ emitEvent: false });
    this.formgroups.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  get stortingenOverig(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formgroups.controls.stortingenOverig;
  }

  addRijStortingenOverig(): void {
    this.stortingenOverig.push(this.createFormOnttrekkingOverig(), {emitEvent: false});
  }

  verwijderRijStortingenOverig(index: number): void {
    this.stortingenOverig.removeAt(index, { emitEvent: false });
    this.formgroups.updateValueAndValidity({ emitEvent: false });
    this.formgroups.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroupAuto(item?: SpecificatiePriveGebruikAuto): FormGroup {
    return new FormGroup({
      autoWordtPriveGebruikt: new FormControl<boolean | null>(item?.autoWordtPriveGebruikt ?? true),
      kenteken: new FormControl<string | null>(item?.kenteken ?? null),
      catalogusPrijs: new FormControl<number | null>(item?.catalogusPrijs ?? null),
      periodePriveGebruik: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.periodePriveGebruik?.begindatum ?? null),
        einddatum: new FormControl<Date | null>(item?.periodePriveGebruik?.einddatum ?? null),
      }),
      kilometerAdministratieAanwezig: new FormControl<boolean | null>(item?.kilometerAdministratieAanwezig ?? true),
      kostenEnLastenVoorEigenRekening: new FormControl<number | null>(item?.kostenEnLastenVoorEigenRekening ?? null),
      priveGebruik: new FormControl<number | null>(item?.priveGebruik ?? null),
    })
  }

  createFormGroupWoning(item?: SpecificatiePriveGebruikWoning): FormGroup {
    return new FormGroup({
      adresWoning: new FormGroup({
        straat: new FormControl<string | null>(item?.adresWoning?.straat ?? null),
        huisNummer: new FormControl<number | null>(item?.adresWoning?.huisNummer ?? null),
        huisNummerToevoeging: new FormControl<string | null>(item?.adresWoning?.huisNummerToevoeging ?? null),
        postcode: new FormControl<string | null>(this.formatPostcode(item?.adresWoning?.postcode) ?? null),
        woonplaats: new FormControl<string | null>(item?.adresWoning?.woonplaats ?? null),
        landCode: new FormControl<string | null>(item?.adresWoning?.landCode ?? "NLD"),
      }),
      periodeBewoning: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.periodeBewoning?.begindatum ?? null),
        einddatum: new FormControl<Date | null>(item?.periodeBewoning?.einddatum ?? null),
      }),
      percentageGerechtigd: new FormControl<number | null>(item?.percentageGerechtigd ?? null),
      berekendeOnttrekking: new FormControl<number | null>(item?.berekendeOnttrekking ?? null),
      wozWaarde: new FormControl<number | null>(item?.wozWaarde ?? null),
    })
  }

  createFormGroupOnttrekkingWoning(item?: SpecificatieOnroerendeZaken): FormGroup {
    return new FormGroup({
      adresOnroerendeZaak: new FormGroup({
        straat: new FormControl<string | null>(item?.adresOnroerendeZaak?.straat ?? null),
        huisNummer: new FormControl<number | null>(item?.adresOnroerendeZaak?.huisNummer ?? null),
        huisNummerToevoeging: new FormControl<string | null>(item?.adresOnroerendeZaak?.huisNummerToevoeging ?? null),
        postcode: new FormControl<string | null>(this.formatPostcode(item?.adresOnroerendeZaak?.postcode) ?? null),
        woonplaats: new FormControl<string | null>(item?.adresOnroerendeZaak?.woonplaats ?? null),
        landCode: new FormControl<string | null>(item?.adresOnroerendeZaak?.landCode ?? "NLD"),
      }),
      waardeEconomischeVerkeer: new FormControl<number | null>(item?.waardeEconomischeVerkeer ?? null),
      boekwaardeOpMonentOnttrekking: new FormControl<number | null>(item?.boekwaardeOpMonentOnttrekking ?? null),
      transactieMetDerde: new FormControl<boolean | null>(item?.transactieMetDerde ?? false),
      taxateurBetrokken: new FormControl<boolean | null>(item?.taxateurBetrokken ?? false),
      naamTaxateur: new FormControl<string | null>(item?.naamTaxateur ?? null),
    })
  }

  createFormOnttrekkingOverig(item?: OmschrijvingEnBedrag): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
    })
  }

  private updateFormAuto(loon: SpecificatiePriveGebruikAuto[]): void {
    let lijst = loon || [];
    const formArray = this.privegebruikAuto;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroupAuto(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  private updateFormWoning(loon: SpecificatiePriveGebruikWoning[]): void {
    let lijst = loon || [];
    const formArray = this.privegebruikWoning;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroupWoning(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  private updateFormOnttrekkingOg(loon: SpecificatieOnroerendeZaken[]): void {
    let lijst = loon || [];
    const formArray = this.onttrekkingWoning;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroupOnttrekkingWoning(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  private updateFormOnttrekkingenOverig(loon: OmschrijvingEnBedrag[]): void {
    let lijst = loon || [];
    const formArray = this.onttrekkingOverig;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormOnttrekkingOverig(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  private updateFormStortingenOg(loon: SpecificatieOnroerendeZaken[]): void {
    let lijst = loon || [];
    const formArray = this.stortingWoning;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroupOnttrekkingWoning(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  private updateFormStortingenOverig(loon: OmschrijvingEnBedrag[]): void {
    let lijst = loon || [];
    const formArray = this.stortingenOverig;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormOnttrekkingOverig(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const idx = this.indexOnderneming;
    const formValue = this.formgroups.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};
        const updatedOnderneming: Partial<Onderneming> = {
          ...onderneming,
          onttrekkingPriveGebruikAuto: formValue.gebruikAuto,
          onttrekkingPriveGebruikWoning: formValue.gebruikWoning,
          onttrekkingOG: formValue.onttrekkingOg,
          onttrekkingenOverig: formValue.onttrekkingOverig,
          stortingOG: formValue.stortingenOg,
          stortingenOverig: formValue.stortingenOverig,
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner
        ? {
          ...currentEntity.aangifte,
          partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
        }
        : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }

  formatPostcode(postcode: string | null | undefined): string {
    if (!postcode) return ''; // Retourneer een lege string als postcode null of undefined is

    // Verwijder alle niet-alfanumerieke tekens (bijv. spaties)
    const cleanedPostcode = postcode.replace(/[^a-zA-Z0-9]/g, '');

    // Zorg voor 4 cijfers en 2 hoofdletters
    if (cleanedPostcode.length >= 4) {
      const cijfers = cleanedPostcode.substring(0, 4).replace(/\D/g, ''); // Alleen cijfers
      const letters = cleanedPostcode.substring(4, 6).replace(/[^a-zA-Z]/g, '').toUpperCase(); // Alleen letters
      return `${cijfers}${letters}`;
    }

    return cleanedPostcode; // Als de postcode te kort is, retourneer de schoongemaakte versie
  }

  onKentekenChangeControl(autoControl: FormGroup): void {
    if (this.isCheckingRdw) {
      return;
    }
    const kentekenControl = autoControl.get('kenteken')?.value;
    const bijtelling = autoControl.get('priveGebruik')?.value;
    const periodePriveGebruik = autoControl.get('periodePriveGebruik')?.value;
    const beginDatumProcessed = new Date(periodePriveGebruik.begindatum).toISOString();
    const eindDatumProcessed = new Date(periodePriveGebruik.einddatum).toISOString();
    let privebetaald = autoControl.get('kostenEnLastenVoorEigenRekening')?.value;
    if (kentekenControl && beginDatumProcessed > new Date('1970-01-01').toISOString() && eindDatumProcessed > new Date('1970-01-01').toISOString() && bijtelling == undefined) {
      if (privebetaald == undefined) {
        privebetaald = 0;
      }
      this.isCheckingRdw = true;
      if (beginDatumProcessed && eindDatumProcessed) {
        this.dataService.getKentekenGegevens(kentekenControl.toUpperCase(), privebetaald, beginDatumProcessed, eindDatumProcessed).subscribe({
          next: (response) => {
            if (response.length > 0 && response[0]?.catalogusprijs) {
              const catalogusPrijs = response[0].catalogusprijs;
              const priveGebruik = response[0].bijtelling;
              autoControl.patchValue({catalogusPrijs, priveGebruik});
            } else {
              this.isCheckingRdw = false;
              console.error('Kenteken niet gevonden.');
            }
          },
          error: (error) => {
            this.isCheckingRdw = false;
            console.error('Fout bij ophalen van gegevens:', error);
          },
          complete: () => {
            this.isCheckingRdw = false;
          }
        });
      } else {
        this.isCheckingRdw = false;
      }
    }
  }
}
